import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from 'react-oidc-context'
import { apiClient } from '../api/client'
import type { GrantCode } from './grants'

/**
 * Vekil modu bilgisi (089). `active=false` iken diğer alanlar `null`'dır — alan her zaman gelir,
 * böylece "yok" ile "false" ayırt edilmek zorunda kalınmaz.
 */
export interface MeProxyInfo {
  active: boolean
  /** İşlemi **fiilen yapan** (giriş yapmış gerçek) kullanıcının kimliği. */
  actingUserId: number | null
  /** Uyarı şeridinde ve seçim kutusunda gösterilen gerçek kullanıcı adı. */
  actingDisplayName: string | null
  /**
   * Kipin türü (126). Vekalette `PROXY`; asistanlıkta yetki seviyesini de taşır.
   * `active=false` iken `null`. Sunucu bu alanı **ekledi**, mevcut alanlar değişmedi.
   *
   * <p>`actingHasOwnProfile` ile **aynı gerekçeyle opsiyonel**: ikisi de bu turda doğdu ve frontend
   * imajı backend'den önce yayına girebilir (THY'de iki ayrı depo, iki ayrı pipeline). Okuyanlar
   * zaten korunuyor (`useProxyMode` → `?? null`, `personaLabelWithAuthority` bilinmeyen değerde
   * çıplak adı döndürür), ama tipin "her zaman gelir" demesi o korumaları gereksiz gösterip birinin
   * silmesine davetiye çıkarırdı — `roleIds`'in javadoc'undaki akıl yürütmenin aynısı.
   */
  kind?: 'PROXY' | 'ASSISTANT_FULL' | 'ASSISTANT_LIMITED' | null
  /**
   * Persona kipinde: **gerçek kullanıcının** kendi rolü var mı? Kendi personasında `false` döner
   * ve anlamsızdır — orada soru üst düzey `roleIds` ile cevaplanır.
   *
   * <p>Bu alan olmadan soru cevaplanamıyordu: persona kipinde `roleIds` **hedefin**dir.
   *
   * <p><b>Opsiyonel, çünkü gerçekten eksik gelebilir:</b> alan 21.10.2026'da doğdu ve frontend
   * imajı backend'den önce yayına girebilir (THY'de iki ayrı depo, iki ayrı pipeline). Zorunlu
   * işaretlemek tipi yalancı yapardı; okuyan taraf `!== false` ile **fail-open** davranır
   * (`useProxyMode.hasOwnProfile`), çünkü yanlış tarafa düşmenin bedeli kullanıcının kendi
   * kimliğine dönememesidir.
   */
  actingHasOwnProfile?: boolean
}

/**
 * {@code GET /api/v1/me} yanıtı (auth-contract.md).
 *
 * **Vekil modunda üst düzey alanlar adına çalışılan kişinindir** (089): `userId`, `displayName`,
 * `roles` ve `grants` hedefin değerlerini taşır. Bu bilinçlidir — menü, rota koruması ve butonlar
 * zaten `grants`'tan beslendiği için arayüz hiçbir ek kod olmadan doğru davranır. Gerçek kullanıcı
 * `proxy` altındadır ve yalnız uyarı şeridi ile seçim kutusu tarafından okunur.
 */
export interface MeResponse {
  userId: number
  userName: string
  displayName: string
  email?: string
  /** Personel sicil no (OIDC employee_id claim'inden; JIT provisioning). Boş olabilir. */
  employeeId?: string
  langKey?: string
  /**
   * Aktif rollerin **kimlikleri** — rol ayrımı yapan her karşılaştırmanın tek meşru girdisi
   * ({@link ROLE_IDS} ile eşleştirilir). `roles` ile **aynı** kümeyi aynı sırada taşır.
   *
   * <p>**Opsiyonel işaretli çünkü alan gerçekten eksik gelebilir:** yeni frontend, `roleIds`
   * eklenmemiş bir backend'e karşı çalışırken (rolling deploy penceresi) alan hiç gelmez.
   * Okuyanlar `?? []` ile korunur → **hiçbir şey gizlenmez** (fail-open). Bu bilinçlidir: gizlemeler
   * kozmetiktir, asıl koruma backend'de yetkiyle yapılır; fail-closed davranmak GM'ye kendi
   * verisini göstermemek olurdu. Tipin `number[]` (zorunlu) kalması, `?? []` korumasını
   * "gereksiz" gösterip birinin silmesine davetiye çıkarırdı.
   */
  roleIds?: number[]
  /** Aktif rollerin görünen adları — **yalnız gösterim**; karşılaştırmada `roleIds` kullanılır. */
  roles: string[]
  grants: string[]
  /**
   * HR erişim kapısı (099) bu kullanıcıyı reddetti mi. `true` iken `grants` her zaman boştur ama
   * `roles`/`roleIds` kullanıcının gerçek atamasını olduğu gibi taşır (contracts/me-contract.md).
   * `true` görüldüğünde normal uygulama kabuğu hiç kurulmadan dedike bir sayfa gösterilir.
   *
   * <p>**Opsiyonel işaretli — `roleIds` ile aynı gerekçe:** yeni frontend, bu alanı henüz eklememiş
   * bir backend'e karşı çalışırken (rolling deploy penceresi) alan hiç gelmeyebilir. Okuyan taraf
   * (`AccessGate`) `data?.accessRestricted` ile korunur → eksik alan **fail-open** (kısıtlama
   * uygulanmaz) davranır; bu, `contracts/me-contract.md`'nin "eski davranış sürdürülür, güvenlik
   * açığı OLUŞMAZ" garantisiyle tutarlıdır (asıl koruma zaten backend'de — boş `grants`).
   */
  accessRestricted?: boolean
  proxy: MeProxyInfo
  /**
   * IT Admin görev içeriği maskesi bu kimlik için etkin mi (129). `true` iken görevlerin başlık ve
   * açıklaması (taraf olunanlar hariç) `***` gelir. Yalnız **sunumsal** kararlar için okunur:
   * başlık/açıklama kolonlarının sıralama oku çizilmez (sunucu o sıralamayı zaten uygulamaz).
   * Maskenin kendisi sunucudadır. Okuyanlar ayrı bir hook değil doğrudan `useCurrentUser().data`
   * kullanır. Karar, bu modülün test dosyalarında tek tek taklit edildiği döneme aittir; taklit artık
   * tek yerdedir (`test/currentUserMock.ts`) ve yeni bir export yalnız oraya eklenir — eklenmezse
   * `tsc -b` kırmızı döner.
   *
   * <p>**Opsiyonel — `roleIds` ile aynı gerekçe:** yeni frontend alanı henüz taşımayan bir backend'e
   * karşı çalışabilir; eksik alan `false` sayılır.
   */
  actionContentMasked?: boolean
}

/**
 * Oturumdaki kullanıcı profili + efektif yetkiler (FR-013/FR-016). Yalnız kimlik doğrulandıktan sonra
 * çağrılır (`enabled`); TanStack Query önbelleğe alır → oturum boyunca tek çağrı (LAST_LOGIN_TIME yan
 * etkisi de bir kez). 401/403 yeniden denenmez (global retry politikası).
 */
export function useCurrentUser() {
  const auth = useAuth()
  return useQuery({
    queryKey: ['me'],
    enabled: auth.isAuthenticated,
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await apiClient.get<MeResponse>('/me')
      return data
    },
  })
}

/**
 * Efektif yetkilere göre görünürlük yardımcıları (FR-016). `hasGrant(code)` yalnız UX içindir; asıl
 * koruma backend'dedir (FR-009). Yetkiler yüklenmeden `hasGrant` `false` döner (fail-closed görünüm).
 */
export function useGrants() {
  const { data, isLoading } = useCurrentUser()
  const grants = useMemo(() => new Set(data?.grants ?? []), [data])
  const hasGrant = useMemo(() => (code: GrantCode) => grants.has(code), [grants])
  const hasAnyGrant = useMemo(
    () => (...codes: GrantCode[]) => codes.some((c) => grants.has(c)),
    [grants],
  )
  return { grants, hasGrant, hasAnyGrant, isLoading }
}

/** Görünen ad önceliği: ad soyad > kullanıcı adı > e-posta (FR-013). */
export function useDisplayName(): string | undefined {
  const { data } = useCurrentUser()
  if (!data) return undefined
  return data.displayName || data.userName || data.email
}
